import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  Signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DsDatepickerConfiguration, DsFormsModule } from '@bmw-ds/components';
import { Locale } from 'date-fns';
import { de, enGB, es, fr, nl, pt } from 'date-fns/locale';

import { I18nService, useI18n } from '../../../i18n/services/i18n.service';
import { FilterCriteria, TimeAggregation } from '../../model/reporting.model';

@Component({
  selector: 'cdf-reporting-date-filter',
  imports: [DsFormsModule, ReactiveFormsModule, ReactiveFormsModule, FormsModule],
  template: `
    <div class="reporting-date-filter-grid">
      <ds-form-field>
        <label ds-label for="timeaggregation">{{ i18n('reporting.aggregation') }}</label>
        <ds-select
          id="timeaggregation"
          [isFilterable]="false"
          [showClearAll]="false"
          [options]="timeAggregations()"
          [(ngModel)]="selectedTimeAggregation"
        />
      </ds-form-field>

      <ds-form-field>
        <label ds-label for="range">{{ i18n('reporting.timeRange') }}</label>
        <ds-datepicker id="range" [config]="config()">
          <input ds-date id="startDate" [(ngModel)]="startDate" />
          <input ds-date id="endDate" [(ngModel)]="endDate" />
        </ds-datepicker>
      </ds-form-field>
    </div>
  `,
  styleUrl: './reporting-date-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportingDateFilterComponent {
  private static readonly localeMapping: Record<string, Locale> = {
    'en-GB': enGB,
    'de-DE': de,
    'es-ES': es,
    'fr-FR': fr,
    'nl-NL': nl,
    'pt-PT': pt,
  };

  private readonly i18nService = inject(I18nService);
  protected readonly i18n = useI18n();

  readonly initialFilterCriteria = input<FilterCriteria>();
  readonly optionsChanged = output<Partial<FilterCriteria>>();

  protected readonly config: Signal<Partial<DsDatepickerConfiguration>> = computed(() => {
    const activeLanguage = this.i18nService.activeLanguage();
    // Justification: only value time of resolution is deprecated, which we are not using here
    // noinspection JSDeprecatedSymbols
    return {
      allowDirectInput: false,
      showClearAll: false,
      selectionMode: 'range',
      resolution: this.selectedTimeAggregation() === TimeAggregation.DAILY ? 'days' : 'months',
      timezone: 'UTC',
      locale: ReportingDateFilterComponent.localeMapping[activeLanguage ?? 'en-GB'] ?? enGB,
    };
  });

  protected readonly timeAggregations = computed(() =>
    Object.values(TimeAggregation).map((timeAggregation) => ({
      id: timeAggregation,
      label: this.i18n.enum(timeAggregation, 'reporting.aggregationValues'),
    })),
  );

  protected readonly selectedTimeAggregation = linkedSignal(
    () => this.initialFilterCriteria()?.timeAggregation ?? TimeAggregation.MONTHLY,
  );
  protected readonly startDate = linkedSignal(() => this.initialFilterCriteria()?.from);
  protected readonly endDate = linkedSignal(() => this.initialFilterCriteria()?.to);

  constructor() {
    effect(() => {
      const from = this.startDate();
      const to = this.endDate();
      if (from && to) {
        this.optionsChanged.emit({
          from: from,
          to: to,
          timeAggregation: this.selectedTimeAggregation(),
        });
      }
    });
  }
}
